import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Modal, ModalDialog, Typography } from "@mui/joy";
import React from "react";

const TERMS_URL = "https://dynamicelevatorcontrol.com/wp-content/uploads/2025/03/Warranty.pdf";

interface SmsTermsDialogProps {
	open: boolean;
	onResolved: () => void;
}

/**
 * Blocking consent after login until the user accepts (with mobile + checkbox) or declines.
 */
export default function SmsTermsDialog(props: SmsTermsDialogProps) {
	const [phone, setPhone] = React.useState("");
	const [ackTerms, setAckTerms] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (props.open) {
			setPhone("");
			setAckTerms(false);
			setSubmitting(false);
		}
	}, [props.open]);

	const submit = async (accepted: boolean) => {
		if (accepted) {
			const trimmed = phone.trim();
			if (!ackTerms || !trimmed)
				return;
		}
		setSubmitting(true);
		try {
			const res = await fetch("/User/RecordSmsTermsConsent", {
				method: "Post",
				cache: "no-cache",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					accepted,
					checkboxAcknowledged: accepted ? ackTerms : false,
					smsPhone: accepted ? phone.trim() : null,
				}),
			});
			if (!res.ok)
				alert(accepted ? "Enter a mobile number and check the box to accept." : "Could not record your choice.");
			else props.onResolved();
		} finally {
			setSubmitting(false);
		}
	};

	const canAccept = phone.trim().length > 0 && ackTerms && !submitting;

	return (
		<Modal open={props.open}>
			{/*
			  Joy ModalDialog (layout=center) defaults to maxHeight: calc(100% - padding), which turns
			  the dialog into a scroll container. That scrollbar steals width ↔ content reflow oscillates each keystroke.
			  maxHeight: none + overflow visible removes that inner scrollbar for this short form.
			 */}
			<ModalDialog
				sx={{
					maxWidth: 520,
					width: "100%",
					minWidth: 0,
					maxHeight: "none !important",
					overflow: "visible",
				}}
				role="alertdialog">
				<DialogTitle>SMS notifications</DialogTitle>
				<DialogContent sx={{ gap: 2, minWidth: 0, overflow: "visible" }}>
					<Typography level="body-md">
						Enter the mobile number you want us to text. By accepting the{" "}
						<a href={TERMS_URL} target="_blank" rel="noreferrer noopener">
							terms and conditions
						</a>
						, you authorize Dynamic Elevator to send SMS/text messages to that number.
					</Typography>
					<Typography level="body-sm" sx={{ fontWeight: "md" }}>
						Mobile number
					</Typography>
					{/* Plain input avoids decorator/wrapper measuring quirks */}
					<Box
						component="input"
						type="tel"
						autoComplete="tel"
						inputMode="tel"
						placeholder="e.g. +1 555 123 4567"
						value={phone}
						disabled={submitting}
						onChange={(e) => setPhone((e.target as HTMLInputElement).value)}
						sx={{
							boxSizing: "border-box",
							width: "100%",
							minWidth: 0,
							px: 1.25,
							py: 0.875,
							borderRadius: "sm",
							border: "1px solid",
							borderColor: "neutral.outlinedBorder",
							bgcolor: "background.surface",
							color: "text.primary",
							fontSize: "md",
							"&:focus-visible": {
								borderColor: "primary.500",
							},
						}}
					/>
					<Checkbox
						label="I have read and agree to the terms and conditions linked above."
						checked={ackTerms}
						onChange={(e) => setAckTerms(e.target.checked)}
						disabled={submitting}
					/>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" color="neutral" onClick={() => submit(false)} disabled={submitting}>
						Decline
					</Button>
					<Button color="primary" onClick={() => submit(true)} disabled={!canAccept}>
						{submitting ? "Saving…" : "Accept"}
					</Button>
				</DialogActions>
			</ModalDialog>
		</Modal>
	);
}
